import { useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Form as KendoForm, FormElement } from '@progress/kendo-react-form';
import { models } from '../../../utils/cms-client';
import { NotFoundScreen } from '../../base/exception-screens/not-found-screen';
import { IFormContext } from '../../../contexts/form-context';
import Buttons from './buttons';
import Notification from './notification';
import { FullScreenLoadingIndicator } from '../../base/full-screen-loading-indicator';
import FloatingValidationBanner from './floating-validation-banner';
import FormStepperSection from './form-stepper-section';
import FormStep, { FormLabel } from './form-step';
import FormSubmission from './form-submission';
import useFormWizard from './use-form-wizard';

// interfaces
export interface ComponentProps extends RouteComponentProps {
  form?: models.Form;
  formContext?: IFormContext;
  stepIndex?: number;
  submitted?: boolean;
  showError?: boolean;
  submitting?: boolean;
}

const Form = (p: ComponentProps) => {
  const {
    form,
    formSteps,
    formStepIndex,
    formState,
    currentStepIndex,
    setCurrentStepIndex,
    steps,
    currentStepHasLeavePlanGraph,
    formContext,
    setFormContext,
    submitted,
    submitting,
    showError,
    setShowError,
    notFound,
    autosaveIsFetching,
    prefillLoading,
    prefillApplied,
    labelMarkdown,
    validationErrorCount,
    setValidationErrorCount,
    displayConfirmationModal,
    setDisplayConfirmationModal,
    displayRestartWarningModal,
    setDisplayRestartWarningModal,
    stepGoBack,
    setStepGoBack,
    scrollToElement,
    sectionName,
    handleOnStepSubmit,
    shouldShowSteps,
    moveToStepCall,
    handleSubmissionSideEffects,
    setFormStepIndex,
  } = useFormWizard(p);

  useEffect(() => {
    if (submitting || submitted) {
      handleSubmissionSideEffects();
    }
  }, [submitting, submitted, handleSubmissionSideEffects]);

  if (autosaveIsFetching || prefillLoading || !prefillApplied) {
    return <FullScreenLoadingIndicator displayText={'Loading...' as any} />;
  }

  if (submitting || submitted) {
    return (
      <FormSubmission
        submitting={submitting}
        form={form}
        labelMarkdown={labelMarkdown}
        formContext={formContext}
        setFormContext={setFormContext}
      />
    );
  }

  return (
    <>
      <div
        className={`d-flex${
          shouldShowSteps() ? ' flex-column flex-lg-row' : ' flex-column'
        } `}
      >
        {shouldShowSteps() && (
          <FormStepperSection
            currentStepIndex={currentStepIndex}
            steps={steps}
            moveToStepCall={moveToStepCall}
          />
        )}
        <div className="d-flex flex-column flex-lg-grow-1">
          <FormLabel labelMarkdown={labelMarkdown} />
          <div className="flex-grow-1">
            <KendoForm
              initialValues={formState}
              onSubmitClick={handleOnStepSubmit}
              render={(formRenderProps) => {
                return (
                  <div>
                    <FormElement>
                      <fieldset className="k-form-fieldset">
                        <FloatingValidationBanner
                          errorCount={validationErrorCount}
                          onDismiss={() => setValidationErrorCount(0)}
                          setErrorCount={setValidationErrorCount}
                        />
                        <FormStep
                          formSteps={formSteps}
                          currentStepIndex={formStepIndex}
                          formStepIndex={formStepIndex}
                          formRenderProps={formRenderProps}
                          form={form}
                          moveToStepCall={moveToStepCall}
                          formName={formContext.formName}
                          sectionName={sectionName}
                        />
                        <Buttons
                          currentStepIndex={currentStepIndex}
                          setCurrentStepIndex={setCurrentStepIndex}
                          formRenderProps={formRenderProps}
                          form={form}
                          formSteps={formSteps}
                          currentStepHasLeavePlanGraph={
                            currentStepHasLeavePlanGraph
                          }
                          submitting={submitting}
                          displayConfirmationModal={displayConfirmationModal}
                          setDisplayConfirmationModal={
                            setDisplayConfirmationModal
                          }
                          displayRestartWarningModal={
                            displayRestartWarningModal
                          }
                          setDisplayRestartWarningModal={
                            setDisplayRestartWarningModal
                          }
                          stepGoBack={stepGoBack}
                          setStepGoBack={setStepGoBack}
                          steps={steps}
                          setFormStepIndex={setFormStepIndex}
                          scrollToElement={scrollToElement}
                          sectionName={sectionName}
                        />
                        {notFound && <NotFoundScreen />}
                      </fieldset>
                    </FormElement>
                  </div>
                );
              }}
            />
          </div>
        </div>
      </div>
      <Notification showError={showError} setShowError={setShowError} />
    </>
  );
};

export default Form;
